import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import "./CryptoDepositModal.css";

import walletIcon from "./../../../../assets/svg/wallet.svg";
import closeIcon from "./../../../../assets/svg/close.svg";
import { UserWallet } from "entities/user";

import { NETWORK_LABELS } from "entities/network";
import { CURRENCY_LABELS } from "entities/currency";

interface CryptoDepositModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (currency: string, network: string, address: string) => void;
  wallets: UserWallet[];
  isLoading?: boolean;
}

export const CryptoDepositModal: React.FC<CryptoDepositModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  wallets,
  isLoading,
}) => {
  const [selectedCurrency, setSelectedCurrency] = useState<string>("");
  const [selectedNetwork, setSelectedNetwork] = useState<string>("");

  useEffect(() => {
    if (isOpen && wallets.length > 0) {
      setSelectedCurrency(wallets[0].currency);
      setSelectedNetwork(wallets[0].network);
    }
  }, [isOpen, wallets]);

  const networkSelectorRef = useRef<HTMLDivElement>(null);
  const networkHeightRef = useRef(0);

  const uniqueCurrencies = Array.from(new Set(wallets.map((w) => w.currency)));

  const availableNetworks = wallets
    .filter((w) => w.currency === selectedCurrency)
    .map((w) => w.network);

  useLayoutEffect(() => {
    const el = networkSelectorRef.current;
    if (!el) return;

    const newHeight = el.scrollHeight;
    const prevHeight = networkHeightRef.current;
    networkHeightRef.current = newHeight;

    if (prevHeight === 0 || prevHeight === newHeight) return;

    el.style.overflow = "hidden";
    el.style.height = `${prevHeight}px`;

    requestAnimationFrame(() => {
      el.style.transition = "height 0.3s ease";
      el.style.height = `${newHeight}px`;
    });

    const cleanup = () => {
      el.style.height = "";
      el.style.overflow = "";
      el.style.transition = "";
    };
    el.addEventListener("transitionend", cleanup, { once: true });
  }, [availableNetworks.join(",")]);

  const handleCurrencyChange = (currency: string) => {
    setSelectedCurrency(currency);
    const networks = wallets.filter((w) => w.currency === currency);
    if (networks.length > 0) {
      setSelectedNetwork(networks[0].network);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const wallet = wallets.find(
      (w) => w.currency === selectedCurrency && w.network === selectedNetwork,
    );
    if (wallet) {
      onConfirm(selectedCurrency, selectedNetwork, wallet.address);
    }
  };

  const [visible, setVisible] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
    } else {
      const timer = setTimeout(() => setVisible(false), 250);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!visible) return null;

  return (
    <div className={`crypto-deposit-modal-overlay${!isOpen ? " closing" : ""}`} onClick={handleBackdropClick}>
      <div className={`crypto-deposit-modal${!isOpen ? " closing" : ""}`}>
        <div className="crypto-deposit-modal-header">
          <img
            className="crypto-deposit-modal-logo"
            src={walletIcon}
            alt="Wallet logo"
          />
          <h2 className="crypto-deposit-modal-title">Select Cryptocurrency</h2>
          <button className="close-button" onClick={onClose}>
            <img src={closeIcon} alt="Close icon" />
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="crypto-deposit-form">
          {isLoading ? (
            <div className="loading-state">Loading wallets...</div>
          ) : wallets.length === 0 ? (
            <div className="loading-state">No wallets available.</div>
          ) : (
            <>
              <div className="form-group">
                <label>Currency:</label>
                <div className="currency-selector">
                  {uniqueCurrencies.map((currency) => (
                    <button
                      key={currency}
                      type="button"
                      className={`currency-button${selectedCurrency === currency ? " active" : ""}`}
                      onClick={() => handleCurrencyChange(currency)}
                    >
                      {CURRENCY_LABELS[currency] || currency}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Network:</label>
                <div className="network-selector" ref={networkSelectorRef}>
                  {availableNetworks.map((network) => (
                    <button
                      key={network}
                      type="button"
                      className={`network-button${selectedNetwork === network ? " active" : ""}`}
                      onClick={() => setSelectedNetwork(network)}
                    >
                      {NETWORK_LABELS[network] || network}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="crypto-deposit-form__button cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="crypto-deposit-form__button confirm"
              disabled={!selectedCurrency || !selectedNetwork}
            >
              Continue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
